// Padrón de la cadena en `retail-org`: tiendas, zonas y usuarios retail de Tiendas y Vendedores.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    listRetailUsers,
    listStores,
    listZones,
} from '@/api/services/retail-org/retail-org.client';
import { useAuth } from '@/hooks/useAuth';
import {
    resolveInventoryRetailer,
    type InventoryRetailerProblem,
} from '../inventario/lib/inventoryRetailer';

const KEY = ['retail-admin', 'roster'] as const;

const PROBLEM_MESSAGE: Record<InventoryRetailerProblem, string> = {
    NONE: 'Tu usuario no tiene una cadena asignada, así que no podemos leer sus tiendas ni sus vendedores. Pide a tu administrador que te asigne la cadena que operas.',
    MANY: 'Tienes varias cadenas asignadas y esta pantalla trabaja con una sola. Pide a tu administrador que te deje solo la cadena que operas.',
};

/** La cadena del scope RBAC, igual que Inventario; sin una sola, un error listo para la pantalla. */
export function useRosterRetailer(): { retailerId: string | null; problemError: Error | null } {
    const { roleAssignments } = useAuth();
    return useMemo(() => {
        const { retailerId, problem } = resolveInventoryRetailer(roleAssignments);
        return { retailerId, problemError: problem ? new Error(PROBLEM_MESSAGE[problem]) : null };
    }, [roleAssignments]);
}

export function useRosterStores(retailerId: string | null) {
    return useQuery({
        queryKey: [...KEY, 'stores', retailerId],
        queryFn: () => listStores(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 5 * 60_000,
    });
}

/** Zonas para nombrar la de cada tienda; si fallan, la zona queda vacía y la lista sigue. */
export function useRosterZones(retailerId: string | null) {
    return useQuery({
        queryKey: [...KEY, 'zones', retailerId],
        queryFn: () => listZones(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 5 * 60_000,
        retry: false,
    });
}

export function useRosterUsers(retailerId: string | null) {
    return useQuery({
        queryKey: [...KEY, 'users', retailerId],
        queryFn: () => listRetailUsers(retailerId ?? ''),
        enabled: retailerId !== null,
        staleTime: 5 * 60_000,
    });
}

/** El usuario del padrón, de la misma consulta que la lista; `null` si no está (o aún no carga). */
export function useRosterUser(retailerId: string | null, retailUserId: string) {
    const users = useRosterUsers(retailerId);
    const user = users.data?.find((candidate) => candidate.retailUserId === retailUserId) ?? null;
    return { user, isPending: users.isPending, isError: users.isError };
}
